import React from 'react'
import { motion } from 'framer-motion'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  ArrowLeft, 
  BarChart3, 
  MousePointer, 
  Smartphone, 
  Shield, 
  Users,
  Calendar,
  Brain,
  Target,
  TrendingUp,
  CheckCircle,
  Clock,
  ListTodo,
  Timer,
  Kanban,
  Play,
  CheckSquare,
  User,
  Workflow
} from 'lucide-react'

export const TaskManagementFeatureDetailPage: React.FC = () => {
  const { featureId } = useParams()
  const { t } = useTranslation('workforce')

  if (!featureId || featureId !== 'task-management') {
    return <Navigate to="/workforce" replace />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <a 
            href="/workforce" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </a>
          
          <div className="text-center">
            <div className="inline-flex items-center gap-3 bg-purple-600 text-white px-6 py-3 rounded-full mb-6">
              <ListTodo className="w-6 h-6" />
              <span className="font-semibold">{t('features.taskManagement.title')}</span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('taskManagement.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('taskManagement.description')}
            </p>
          </div>
        </motion.div>

        {/* Dashboard Principal */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-purple-50 px-6 py-3 rounded-full mb-6">
                <BarChart3 className="w-6 h-6 text-purple-600" />
                <span className="text-purple-800 font-semibold">{t('taskManagement.dashboard.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.dashboard.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.dashboard.description')}
              </p>
            </div>
            
            <div className="relative mb-8">
              <img 
                src="/images/workforce/tasks_dashboard.png" 
                alt="Dashboard Task Management - Métricas e Produtividade"
                className="w-full rounded-2xl shadow-xl border border-gray-100"
              />
              <div className="absolute top-6 right-6 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                {t('taskManagement.dashboard.monitoring')}
              </div>
              <div className="absolute bottom-6 left-6 bg-black/80 text-white px-4 py-2 rounded-lg text-sm">
                📊 {t('taskManagement.dashboard.realTimeMetrics')}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center border border-purple-200">
                <div className="text-3xl font-bold text-purple-700 mb-2">94%</div>
                <div className="text-sm text-purple-600 font-medium">{t('taskManagement.dashboard.metrics.completion')}</div>
                <div className="text-xs text-purple-500 mt-1">↗ +8% vs mês anterior</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center border border-green-200">
                <div className="text-3xl font-bold text-green-700 mb-2">156</div>
                <div className="text-sm text-green-600 font-medium">{t('taskManagement.dashboard.metrics.active')}</div>
                <div className="text-xs text-green-500 mt-1">42 {t('taskManagement.dashboard.metrics.inProgress')}</div>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200">
                <div className="text-3xl font-bold text-blue-700 mb-2">4.2h</div>
                <div className="text-sm text-blue-600 font-medium">{t('taskManagement.dashboard.metrics.averageTime')}</div>
                <div className="text-xs text-blue-500 mt-1">{t('taskManagement.dashboard.metrics.perTask')}</div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center border border-orange-200">
                <div className="text-3xl font-bold text-orange-700 mb-2">89%</div>
                <div className="text-sm text-orange-600 font-medium">{t('taskManagement.dashboard.metrics.onTime')}</div>
                <div className="text-xs text-orange-500 mt-1">↗ +12% {t('taskManagement.dashboard.metrics.vsPrevious')}</div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Kanban Board */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-indigo-50 px-6 py-3 rounded-full mb-6">
                <Kanban className="w-6 h-6 text-indigo-600" />
                <span className="text-indigo-800 font-semibold">{t('taskManagement.kanban.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.kanban.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.kanban.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div>
                <img 
                  src="/images/workforce/tasks_kanban.png" 
                  alt="Kanban Board - Interface Visual de Tarefas"
                  className="w-full rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('taskManagement.kanban.description2')}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MousePointer className="w-6 h-6 text-indigo-600" />
                    {t('taskManagement.kanban.visualControls')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      {t('taskManagement.kanban.features.0')}
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      {t('taskManagement.kanban.features.1')}
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      {t('taskManagement.kanban.features.2')}
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      {t('taskManagement.kanban.features.3')}
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Workflow className="w-6 h-6 text-purple-600" />
                    {t('taskManagement.kanban.taskStates')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium">BACKLOG</span>
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">{t('taskManagement.kanban.states.backlog')}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium">IN_PROGRESS</span>
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">{t('taskManagement.kanban.states.inProgress')}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium">COMPLETED</span>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">{t('taskManagement.kanban.states.completed')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>


        {/* Task Assignment */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-green-50 px-6 py-3 rounded-full mb-6">
                <User className="w-6 h-6 text-green-600" />
                <span className="text-green-800 font-semibold">{t('taskManagement.assignment.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.assignment.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.assignment.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div>
                <img 
                  src="/images/workforce/task_assignments.png" 
                  alt="Task Assignment - Sistema de Atribuição"
                  className="w-full rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('taskManagement.assignment.description2')}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Target className="w-6 h-6 text-green-600" />
                    {t('taskManagement.assignment.management')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <CheckSquare className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium">{t('taskManagement.assignment.features.0.title')}</div>
                        <div className="text-sm text-gray-500">{t('taskManagement.assignment.features.0.description')}</div>
                      </div>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium">{t('taskManagement.assignment.features.1.title')}</div>
                        <div className="text-sm text-gray-500">{t('taskManagement.assignment.features.1.description')}</div>
                      </div>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-medium">{t('taskManagement.assignment.features.2.title')}</div>
                        <div className="text-sm text-gray-500">{t('taskManagement.assignment.features.2.description')}</div>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                    {t('taskManagement.assignment.states.title')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 text-xs font-bold">P</span>
                      </div>
                      <span className="text-sm font-medium">PLANNED</span>
                      <span className="ml-auto text-xs text-gray-500">{t('taskManagement.assignment.states.planned')}</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-xs font-bold">A</span>
                      </div>
                      <span className="text-sm font-medium">ASSIGNED</span>
                      <span className="ml-auto text-xs text-gray-500">{t('taskManagement.assignment.states.assigned')}</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-xs font-bold">C</span>
                      </div>
                      <span className="text-sm font-medium">COMPLETED</span>
                      <span className="ml-auto text-xs text-gray-500">{t('taskManagement.assignment.states.completed')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Interface Mobile */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-orange-50 px-6 py-3 rounded-full mb-6">
                <Smartphone className="w-6 h-6 text-orange-600" />
                <span className="text-orange-800 font-semibold">{t('taskManagement.mobile.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.mobile.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.mobile.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6 border border-orange-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Play className="w-6 h-6 text-orange-600" />
                    {t('taskManagement.mobile.workSessions')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      {t('taskManagement.mobile.sessionFeatures.0')}
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      {t('taskManagement.mobile.sessionFeatures.1')}
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      {t('taskManagement.mobile.sessionFeatures.2')}
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      {t('taskManagement.mobile.sessionFeatures.3')}
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckSquare className="w-6 h-6 text-purple-600" />
                    {t('taskManagement.mobile.interface')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      {t('taskManagement.mobile.interfaceFeatures.0')}
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      {t('taskManagement.mobile.interfaceFeatures.1')}
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      {t('taskManagement.mobile.interfaceFeatures.2')}
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      {t('taskManagement.mobile.interfaceFeatures.3')}
                    </li>
                  </ul>
                </div>
              </div>
              
              <div>
                <img 
                  src="/images/workforce/task_mobile.png" 
                  alt="Interface Mobile - Task Management PWA"
                  className="rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('taskManagement.mobile.description2')}
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Task Planning */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-20"
        >
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-teal-50 px-6 py-3 rounded-full mb-6">
                <Calendar className="w-6 h-6 text-teal-600" />
                <span className="text-teal-800 font-semibold">{t('taskManagement.planning.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('taskManagement.planning.title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('taskManagement.planning.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div>
                <img 
                  src="/images/workforce/task_planning.png" 
                  alt="Task Planning - Integração com Turnos"
                  className="w-full rounded-2xl shadow-xl border border-gray-100"
                />
                <div className="mt-4 text-center text-sm text-gray-600">
                  {t('taskManagement.planning.description2')}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl p-6 border border-teal-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-teal-600" />
                    {t('taskManagement.planning.integration')}
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      {t('taskManagement.planning.features.0')}
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      {t('taskManagement.planning.features.1')}
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      {t('taskManagement.planning.features.2')}
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      {t('taskManagement.planning.features.3')}
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Brain className="w-6 h-6 text-indigo-600" />
                    {t('taskManagement.assignment.algorithms')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                        <span className="text-indigo-600 text-xs font-bold">1</span>
                      </div>
                      <span className="text-sm">{t('taskManagement.assignment.steps.0')}</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-xs font-bold">2</span>
                      </div>
                      <span className="text-sm">{t('taskManagement.assignment.steps.1')}</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-xs font-bold">3</span>
                      </div>
                      <span className="text-sm">{t('taskManagement.assignment.steps.2')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>



        {/* Especificações Técnicas */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 shadow-2xl text-white">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-3 bg-white/10 px-6 py-3 rounded-full mb-6">
                <Shield className="w-6 h-6 text-white" />
                <span className="text-white font-semibold">{t('taskManagement.technical.subtitle')}</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                {t('taskManagement.technical.title')}
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                {t('taskManagement.technical.description')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                  <ListTodo className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{t('taskManagement.technical.taskEngine.title')}</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• {t('taskManagement.technical.taskEngine.features.0')}</li>
                  <li>• {t('taskManagement.technical.taskEngine.features.1')}</li>
                  <li>• {t('taskManagement.technical.taskEngine.features.2')}</li>
                  <li>• {t('taskManagement.technical.taskEngine.features.3')}</li>
                </ul>
              </div>
              
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Timer className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{t('taskManagement.technical.timeManagement.title')}</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• {t('taskManagement.technical.timeManagement.features.0')}</li>
                  <li>• {t('taskManagement.technical.timeManagement.features.1')}</li>
                  <li>• {t('taskManagement.technical.timeManagement.features.2')}</li>
                  <li>• {t('taskManagement.technical.timeManagement.features.3')}</li>
                </ul>
              </div>
              
              <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{t('taskManagement.technical.analytics.title')}</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• {t('taskManagement.technical.analytics.features.0')}</li>
                  <li>• {t('taskManagement.technical.analytics.features.1')}</li>
                  <li>• {t('taskManagement.technical.analytics.features.2')}</li>
                  <li>• {t('taskManagement.technical.analytics.features.3')}</li>
                </ul>
              </div>
            </div>
          </div>
        </motion.section>


      </div>
    </div>
  )
}

export default TaskManagementFeatureDetailPage